<template>
  <div class="container-fluid">
    <div class="row mobile flex-nowrap">
      <MobileSideBar id="mobile" />
      <SideBar id="desktop" />
      <main class="col bg-light renewals-view">
        <UserDetailsModal
          :visible="showUserDetails"
          :user="selectedUser"
          :toggle-popup="closeUserDetails"
        />
        <header class="renewals-header">
          <div>
            <h1>{{ notificationType === 'expired' ? 'Expired SIM closure warnings' : 'Internet extension notifications' }}</h1>
            <p>Emails are not sent automatically. Open the email, send it from your email program, then save the sent record to the user's Notes.</p>
          </div>
        </header>
        <section class="renewals-controls">
          <div class="renewals-controls-row">
            <label class="form-label mb-0">Notification type
              <select v-model="notificationType" class="form-select" @change="loadCandidates">
                <option value="upcoming">Expires in 7 days</option>
                <option value="expired">Expired active SIMs – closure warning</option>
              </select>
            </label>
            <label v-if="notificationType === 'upcoming'" class="form-label mb-0">Expiry date
              <input v-model="targetDate" type="date" class="form-control" @change="loadCandidates" />
            </label>
            <button type="button" class="btn renewals-refresh" :disabled="loading" @click="loadCandidates">Refresh</button>
          </div>
        </section>

        <div v-if="loading" class="alert alert-secondary">Loading eligible SIMs and owner details…</div>
        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
        <div v-else-if="candidates.length === 0" class="alert alert-info">There are no eligible SIMs for this notification.</div>

        <div v-else class="renewals-table-wrap">
          <table class="table table-hover align-middle notification-table">
            <thead><tr><th>Device</th><th>User</th><th>Email</th><th>Language</th><th>Billing</th><th>SIM Notes</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              <tr v-for="candidate in candidates" :key="candidate.sim.id || candidate.sim.phoneNumber">
                <td><strong>{{ candidate.sim.phoneNumber }}</strong><br><small>IMEI {{ candidate.sim.imei || 'missing' }} · <span :class="getBrandClass(candidate.sim.brand)">{{ candidate.sim.brand || 'Huntloc' }}</span><br>Valid until: {{ candidate.sim.validUntil }}</small></td>
                <td>
                  <button type="button" class="btn btn-link p-0 text-start" :disabled="!candidate.ownerPhone" @click="openUserDetails(candidate)">{{ candidate.ownerName || 'No owner' }}</button>
                  <br><small v-if="candidate.ownerPhone">{{ candidate.ownerPhone }}</small>
                </td>
                <td>{{ candidate.email || 'No email' }}</td>
                <td>
                  <select v-model="candidate.language" class="form-select form-select-sm" :aria-label="`Notification language for device ${candidate.sim.phoneNumber}`" @change="rebuildCandidateMessage(candidate)">
                    <option v-for="language in languageOptions" :key="language.value" :value="language.value">{{ language.label }}</option>
                  </select>
                </td>
                <td><span v-if="candidate.teamName" class="text-warning-emphasis">Team pays: {{ candidate.teamName }}</span><span v-else>User pays</span></td>
                <td>{{ candidate.sim.notes || '—' }}</td>
                <td><span :class="candidate.sent ? 'text-primary' : (candidate.email ? 'text-success' : 'text-danger')">{{ candidate.sent ? `Notification sent ${formatSentDate(candidate.sentAt)}` : (candidate.email ? 'Ready for review' : 'Cannot send') }}</span></td>
                <td class="renewals-actions-cell"><div class="renewals-actions">
                  <button type="button" class="btn btn-sm renewals-action-primary" :disabled="!candidate.email" @click.stop.prevent="openEmailClient(candidate)">Open email</button>
                  <button type="button" class="btn btn-sm renewals-action-secondary" @click.stop.prevent="copyEmail(candidate)">Copy text</button>
                  <a class="btn btn-sm renewals-action-secondary" :href="candidate.paymentUrl" target="_blank" rel="noopener">Payment link</a>
                  <button type="button" class="btn btn-sm renewals-action-secondary" :disabled="!candidate.ownerPhone || candidate.saving" @click.stop.prevent="markAsSent(candidate)">{{ candidate.saving ? 'Saving…' : (candidate.sent ? 'Save new send to Notes' : 'Save send to Notes') }}</button>
                </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SideBar from '@/components/SideBar.vue'
import MobileSideBar from '@/components/MobileSideBar.vue'
import UserDetailsModal from '@/components/UserDetailsModal.vue'
import { API_ENDPOINTS } from '@/config/api'
import { buildExpiredSimWarningEmail, buildPaymentUrl, buildRenewalEmail, compareRenewalCandidates, findExpiredSimWarningNote, findRenewalNotificationNote, isActiveSimExpired, isActiveSimExpiringOn, resolveNotificationLanguage } from '@/utils/renewalNotifications'
import { findTeamBySimLinkValue, getTeamLinkLabel } from '@/utils/simTeamLink'

function formatOffsetDate(days) {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() + days)
  const year = date.getFullYear()
  const month = (`0${date.getMonth() + 1}`).slice(-2)
  const day = (`0${date.getDate()}`).slice(-2)
  return `${year}-${month}-${day}`
}

function normalizePhone(value) {
  return String(value || '').replace(/\s+/g, '').replace(/^\+/, '')
}

export default {
  name: 'InternetRenewals',
  components: { SideBar, MobileSideBar, UserDetailsModal },
  data() {
    return {
      targetDate: formatOffsetDate(7), notificationType: 'upcoming', candidates: [], loading: false, error: '',
      selectedUser: { teamsinfo: [], licenses: [], devices: [], notes: [] }, showUserDetails: false,
      languageOptions: [
        { value: 'et', label: 'Estonian' },
        { value: 'lv', label: 'Latvian' },
        { value: 'lt', label: 'Lithuanian' },
        { value: 'en', label: 'English' },
        { value: 'fi', label: 'Finnish' },
        { value: 'ru', label: 'Russian' },
        { value: 'de', label: 'German' }
      ]
    }
  },
  created() { this.loadCandidates() },
  methods: {
    async loadCandidates() {
      this.loading = true
      this.error = ''
      try {
        const [simsResponse, usersResponse, teamsResponse] = await Promise.all([
          axios.get(`${API_ENDPOINTS.TIW_API}/sim/all`),
          axios.get(`${API_ENDPOINTS.TIW_API}/users`),
          axios.get(`${API_ENDPOINTS.TIW_API}/team/list_all`)
        ])
        const todayDate = formatOffsetDate(0)
        const sims = Array.isArray(simsResponse.data) ? simsResponse.data.filter((sim) => (
          this.notificationType === 'expired'
            ? isActiveSimExpired(sim, todayDate)
            : isActiveSimExpiringOn(sim, this.targetDate)
        )) : []
        const users = Array.isArray(usersResponse.data) ? usersResponse.data : []
        const teams = Array.isArray(teamsResponse.data) ? teamsResponse.data : []
        const ownersByImei = new Map()
        users.forEach((user) => (user.devices || []).forEach((device) => {
          const imei = String(device?.imei || device?.imeiNumber || '').trim()
          if (imei) ownersByImei.set(imei, user)
        }))

        const candidates = await Promise.all(sims.map(async (sim) => {
          let owner = ownersByImei.get(String(sim.imei || '').trim())
          if (!owner && sim.imei) {
            try {
              const trackerResponse = await axios.get(`${API_ENDPOINTS.TIW_API}/tracker/${encodeURIComponent(sim.imei)}`)
              const ownerPhone = normalizePhone(trackerResponse.data?.ownerPhone)
              owner = users.find((user) => normalizePhone(user.cellnumber || user.phoneNumber) === ownerPhone)
            } catch (_) { /* The row remains available for manual follow-up. */ }
          }
          const email = String(owner?.email || '').trim()
          const language = resolveNotificationLanguage(sim, owner)
          const message = this.notificationType === 'expired'
            ? buildExpiredSimWarningEmail({ sim, owner, language })
            : buildRenewalEmail({ sim, owner, language })
          const linkedTeam = findTeamBySimLinkValue(sim.pricingModel, teams)
          const teamName = linkedTeam ? getTeamLinkLabel(linkedTeam) : ''
          let sentNote = null
          if (owner?.cellnumber || owner?.phoneNumber) {
            try {
              const activityResponse = await axios.get(`${API_ENDPOINTS.TIW_API}/activity/${encodeURIComponent(owner.cellnumber || owner.phoneNumber)}`)
              sentNote = this.notificationType === 'expired'
                ? findExpiredSimWarningNote(activityResponse.data, sim.phoneNumber)
                : findRenewalNotificationNote(activityResponse.data, sim.phoneNumber)
            } catch (_) { /* Sending remains possible when Notes cannot be read. */ }
          }

          return {
            sim, owner, email, ownerName: String(owner?.username || owner?.name || '').trim(),
            ownerPhone: String(owner?.cellnumber || owner?.phoneNumber || '').trim(),
            language, paymentUrl: buildPaymentUrl(sim, language), message,
            teamName, notificationType: this.notificationType, sent: Boolean(sentNote), sentAt: sentNote?.created || null, saving: false
          }
        }))
        this.candidates = candidates.sort(compareRenewalCandidates)
      } catch (_) {
        this.error = 'Failed to load data. Please try again or check the TIW connection.'
        this.candidates = []
      } finally {
        this.loading = false
      }
    },
    openEmailClient(candidate) {
      const subject = encodeURIComponent(candidate.message.subject)
      const body = encodeURIComponent(candidate.message.body)
      window.location.href = `mailto:${encodeURIComponent(candidate.email)}?subject=${subject}&body=${body}`
    },
    async openUserDetails(candidate) {
      if (!candidate.ownerPhone) return

      this.selectedUser = {
        ...candidate.owner,
        name: candidate.owner?.name || candidate.owner?.username || candidate.ownerName,
        cellnumber: candidate.ownerPhone,
        teamsinfo: Array.isArray(candidate.owner?.teamsinfo) ? candidate.owner.teamsinfo : [],
        licenses: Array.isArray(candidate.owner?.licenses) ? candidate.owner.licenses : [],
        devices: Array.isArray(candidate.owner?.devices) ? candidate.owner.devices : [],
        notes: Array.isArray(candidate.owner?.notes) ? candidate.owner.notes : []
      }
      this.showUserDetails = true

      try {
        const activityResponse = await axios.get(`${API_ENDPOINTS.TIW_API}/activity/${encodeURIComponent(candidate.ownerPhone)}`)
        this.selectedUser.notes = Array.isArray(activityResponse.data) ? activityResponse.data : []
      } catch (_) {
        // The detail view remains usable even if the Notes history cannot be loaded.
      }
    },
    closeUserDetails() {
      this.showUserDetails = false
    },
    rebuildCandidateMessage(candidate) {
      candidate.message = candidate.notificationType === 'expired'
        ? buildExpiredSimWarningEmail({ sim: candidate.sim, owner: candidate.owner, language: candidate.language })
        : buildRenewalEmail({ sim: candidate.sim, owner: candidate.owner, language: candidate.language })
      candidate.paymentUrl = buildPaymentUrl(candidate.sim, candidate.language)
    },
    formatSentDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return `${(`0${date.getDate()}`).slice(-2)}.${(`0${date.getMonth() + 1}`).slice(-2)}.${date.getFullYear()}`
    },
    getBrandClass(brand) {
      const normalizedBrand = String(brand || '').trim().toLowerCase()
      if (normalizedBrand === 'huntloc') return 'sim-brand-huntloc'
      if (normalizedBrand === 'doglo') return 'sim-brand-doglo'
      return ''
    },
    async copyEmail(candidate) {
      const content = `${candidate.message.subject}\n\n${candidate.message.body}`
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(content)
        } else {
          this.copyTextFallback(content)
        }
        alert('Email subject and text have been copied.')
      } catch (_) {
        try {
          this.copyTextFallback(content)
          alert('Email subject and text have been copied.')
        } catch (_) {
          alert('Copying failed. You can still select and copy the text manually.')
        }
      }
    },
    copyTextFallback(content) {
      const textarea = document.createElement('textarea')
      textarea.value = content
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!copied) throw new Error('Copy command was rejected')
    },
    async markAsSent(candidate) {
      if (!candidate.ownerPhone || candidate.saving) return

      const confirmed = window.confirm(
        `Has the email already been sent to ${candidate.email}?\n\nThis button does NOT send an email or change SIM validity. It only saves a sending record to the user's Notes.`
      )
      if (!confirmed) return

      candidate.saving = true
      const note = {
        name: 'admin_note',
        created: Date.now(),
        data: {
          text: `${candidate.notificationType === 'expired' ? 'Aegunud SIMi sulgemishoiatuse e-kiri saadetud' : 'Interneti pikendamise e-kiri saadetud'}: ${candidate.sim.brand || 'Huntloc'}, SIM ${candidate.sim.phoneNumber}, IMEI ${candidate.sim.imei || '-'}, aegub ${candidate.sim.validUntil}, keel ${candidate.language.toUpperCase()}.\nTeema: ${candidate.message.subject}\n\nE-kirja sisu:\n${candidate.message.body}`
        },
        dataJsonStr: null
      }

      try {
        await axios.post(`${API_ENDPOINTS.TIW_API}/activity/${encodeURIComponent(candidate.ownerPhone)}`, note)
        candidate.sent = true
        candidate.sentAt = note.created
      } catch (_) {
        alert("The sending record could not be saved to the user's Notes. Please try again.")
      } finally {
        candidate.saving = false
      }
    }
  }
}
</script>

<style scoped>
#mobile { display: none; }
.renewals-view { padding: 4px 18px 18px; color: #202629; }
.renewals-header { margin: 0 -18px 18px; padding: 20px 22px; border-left: 4px solid #f56600; border-radius: 0 0 9px 9px; background: #202629; color: #fff; }
.renewals-header h1 { margin: 0; font-size: 22px; font-weight: 700; }
.renewals-header p { max-width: 760px; margin: 5px 0 0; color: #c8d0d3; font-size: 11px; font-style: italic; }
.renewals-controls { margin-bottom: 12px; padding: 12px; border: 1px solid #e1e5e6; border-radius: 9px; background: #fbfcfc; }
.renewals-controls-row { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 10px; }
.renewals-controls .form-label { color: #40545e; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.renewals-controls .form-select, .renewals-controls .form-control { min-width: 180px; font-size: 11px; }
.renewals-controls .form-select:focus, .renewals-controls .form-control:focus { border-color: #f56600; box-shadow: 0 0 0 .15rem rgba(245, 102, 0, .16); }
.renewals-refresh, .renewals-action-primary { border: 1px solid #f56600; background: #f56600; color: #fff; font-size: 10px; }
.renewals-refresh:hover, .renewals-action-primary:hover { border-color: #d95700; background: #d95700; color: #fff; }
.renewals-table-wrap { overflow-x: auto; border: 1px solid #e1e5e6; border-radius: 9px; background: #fff; }
.notification-table { min-width: 1150px; margin-bottom: 0; }
.notification-table thead { background: #f1f4f5; }
.notification-table th, .notification-table td { padding: 9px 10px; vertical-align: middle; border-color: #e1e5e6; }
.notification-table thead th { color: #40545e; font-size: 10px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
.notification-table tbody tr:last-child > * { border-bottom: 0; }
.renewals-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.renewals-action-secondary { border: 1px solid #d7dfe2; background: #fff; color: #202629; font-size: 10px; }
.renewals-action-secondary:hover { border-color: #f56600; color: #f56600; }
.notification-help {
  font-size: 10px;
  font-style: italic;
}
.notification-table th,
.notification-table td,
.notification-table select,
.notification-table button,
.notification-table a {
  font-size: 10px;
}
.sim-brand-huntloc,
.sim-brand-doglo {
  display: inline-block;
  min-width: 54px;
  padding: 2px 6px;
  border-radius: 999px;
  text-align: center;
}
.sim-brand-huntloc {
  background-color: #ff9a00;
  color: #000;
}
.sim-brand-doglo {
  background-color: #000;
  color: #fff;
}
@media screen and (max-width: 768px) {
  #mobile { display: flex; }
  #desktop { display: none; }
  .mobile { display: grid; }
  .renewals-view { padding: 4px 10px 12px; }
  .renewals-header { margin: 0 -10px 14px; padding: 16px; }
  .renewals-controls .form-select, .renewals-controls .form-control, .renewals-refresh { width: 100%; }
}
</style>
